<template>
  <div class="page-container">
    <div class="bg-grid"></div>
    <div class="bg-glow"></div>

    <header class="page-header">
      <div class="breadcrumb">
        <router-link to="/" class="breadcrumb-link">{{ i18n.locale === 'zh' ? '首页' : 'Home' }}</router-link>
        <span class="breadcrumb-sep">/</span>
        <span class="breadcrumb-current">{{ i18n.t('pages.uuid.title') }}</span>
      </div>
      <div class="header-content">
        <div class="header-icon">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
            <rect x="3" y="3" width="18" height="18" rx="2"/>
            <path d="M7 7h.01M7 12h.01M7 17h.01M12 7h.01M12 12h.01M12 17h.01M17 7h.01M17 12h.01M17 17h.01"/>
          </svg>
        </div>
        <h1 class="page-title">{{ i18n.t('pages.uuid.title') }}</h1>
        <p class="page-subtitle">{{ i18n.t('pages.uuid.subtitle') }}</p>
      </div>
    </header>

    <main class="page-content">
      <div class="uuid-card">
        <div class="uuid-options">
          <button
            v-for="type in uuidTypes"
            :key="type.value"
            @click="selectedType = type.value"
            :class="['type-btn', { active: selectedType === type.value }]"
          >
            <span class="type-name">{{ type.label }}</span>
            <span class="type-desc">{{ type.desc }}</span>
          </button>
        </div>

        <div class="uuid-display">
          <div class="uuid-value">{{ uuid }}</div>
          <div class="uuid-actions">
            <button @click="regenerate" class="action-btn">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M1 4v6h6M23 20v-6h-6"/>
                <path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 0 1 3.51 15"/>
              </svg>
              {{ i18n.locale === 'zh' ? '重新生成' : 'Regenerate' }}
            </button>
            <button @click="copyUUID" class="action-btn">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <rect x="9" y="9" width="13" height="13" rx="2"/>
                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
              </svg>
              {{ i18n.locale === 'zh' ? '复制' : 'Copy' }}
            </button>
          </div>
        </div>

        <div class="batch-section">
          <label class="batch-label">{{ i18n.locale === 'zh' ? '批量生成' : 'Batch Generate' }}</label>
          <div class="batch-controls">
            <input type="number" v-model="batchCount" min="1" max="100" class="batch-input" />
            <button @click="generateBatch" class="batch-btn">
              {{ i18n.locale === 'zh' ? '生成' : 'Generate' }} {{ batchCount }} {{ i18n.locale === 'zh' ? '个' : '' }}
            </button>
          </div>
          <div v-if="batchUuids.length" class="batch-list">
            <div v-for="(uid, idx) in batchUuids" :key="idx" class="batch-item">{{ uid }}</div>
          </div>
          <div v-if="batchUuids.length" class="batch-actions">
            <button @click="copyBatch" class="copy-all-btn">
              {{ i18n.locale === 'zh' ? '复制全部' : 'Copy All' }}
            </button>
            <button @click="downloadBatch" class="download-btn">
              {{ i18n.locale === 'zh' ? '下载 TXT' : 'Download TXT' }}
            </button>
          </div>
        </div>
      </div>
    </main>
  </div>
</template>

<script>
export default {
  name: 'UUIDGeneratorPage',
  inject: ['i18n'],
  data() {
    return {
      uuid: '',
      selectedType: 'v4',
      batchCount: 10,
      batchUuids: [],
      uuidTypes: [
        { value: 'v4', label: 'UUID v4', desc: '随机' },
        { value: 'v1', label: 'UUID v1', desc: '时间戳' }
      ]
    }
  },
  created() {
    this.generateUUID()
  },
  methods: {
    generateUUID() {
      if (this.selectedType === 'v4') {
        this.uuid = this.generateV4()
      } else {
        this.uuid = this.generateV1()
      }
    },
    generateV4() {
      return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
        const r = Math.random() * 16 | 0
        const v = c === 'x' ? r : (r & 0x3 | 0x8)
        return v.toString(16)
      })
    },
    generateV1() {
      const now = Date.now()
      const clock = Math.random() * 0x3fff | 0
      const node = Array.from({ length: 6 }, () => Math.floor(Math.random() * 256).toString(16).padStart(2, '0')).join('')

      const timeLow = (now & 0xffffffff).toString(16).padStart(8, '0')
      const timeMid = ((now >> 32) & 0xffff).toString(16).padStart(4, '0')
      const timeHigh = (((now >> 48) & 0x0fff) | 0x1000).toString(16).padStart(4, '0')
      const clockSeq = (clock | 0x8000).toString(16).padStart(4, '0')

      return `${timeLow}-${timeMid}-${timeHigh}-${clockSeq}-${node}`
    },
    regenerate() {
      this.batchUuids = []
      this.generateUUID()
    },
    async copyUUID() {
      try {
        await navigator.clipboard.writeText(this.uuid)
      } catch (e) {
        console.error('Copy failed', e)
      }
    },
    generateBatch() {
      this.batchUuids = Array.from({ length: this.batchCount }, () =>
        this.selectedType === 'v4' ? this.generateV4() : this.generateV1()
      )
    },
    async copyBatch() {
      try {
        await navigator.clipboard.writeText(this.batchUuids.join('\n'))
      } catch (e) {
        console.error('Copy failed', e)
      }
    },
    downloadBatch() {
      const blob = new Blob([this.batchUuids.join('\n')], { type: 'text/plain' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `uuids-${Date.now()}.txt`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    }
  },
  watch: {
    selectedType() {
      this.regenerate()
    }
  }
}
</script>

<style scoped>
.page-container {
  min-height: 100vh;
  padding: 40px 24px 60px;
  position: relative;
  overflow: hidden;
}

.bg-grid {
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background-image:
    linear-gradient(rgba(232, 255, 0, 0.03) 1px, transparent 1px),
    linear-gradient(90deg, rgba(232, 255, 0, 0.03) 1px, transparent 1px);
  background-size: 60px 60px;
  pointer-events: none;
  z-index: 0;
}

.bg-glow {
  position: fixed;
  width: 500px;
  height: 500px;
  background: radial-gradient(circle, rgba(232, 255, 0, 0.1) 0%, transparent 70%);
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  filter: blur(100px);
  pointer-events: none;
  z-index: 0;
}

.page-header {
  max-width: 700px;
  margin: 0 auto 50px;
  position: relative;
  z-index: 1;
}

.breadcrumb {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 30px;
  font-family: var(--font-mono);
  font-size: 12px;
}

.breadcrumb-link { color: var(--text-secondary); text-decoration: none; transition: color 0.3s; }
.breadcrumb-link:hover { color: var(--accent-primary); }
.breadcrumb-sep { color: var(--border-color); }
.breadcrumb-current { color: var(--accent-primary); }

.header-content { text-align: center; }

.header-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  background: var(--bg-card);
  border: 1px solid var(--border-color);
  border-radius: 16px;
  color: var(--accent-primary);
  margin-bottom: 20px;
}

.page-title {
  font-family: var(--font-display);
  font-size: 48px;
  letter-spacing: 2px;
  color: var(--text-primary);
  margin-bottom: 12px;
}

.page-subtitle { font-size: 16px; color: var(--text-secondary); font-weight: 300; }

.page-content { max-width: 700px; margin: 0 auto; position: relative; z-index: 1; }

.uuid-card {
  background: var(--bg-card);
  border: 1px solid var(--border-color);
  border-radius: 20px;
  padding: 40px;
}

.uuid-options {
  display: flex;
  gap: 12px;
  margin-bottom: 32px;
}

.type-btn {
  flex: 1;
  padding: 16px 20px;
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  text-align: left;
}

.type-btn:hover { border-color: var(--accent-primary); }
.type-btn.active {
  background: var(--accent-primary);
  color: var(--bg-primary);
  border-color: var(--accent-primary);
}

.type-name {
  display: block;
  font-family: var(--font-mono);
  font-size: 14px;
  font-weight: 700;
  margin-bottom: 4px;
}

.type-desc {
  display: block;
  font-size: 12px;
  opacity: 0.7;
}

.type-btn.active .type-name,
.type-btn.active .type-desc { color: var(--bg-primary); }

.uuid-display {
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: 16px;
  padding: 28px;
  margin-bottom: 32px;
}

.uuid-value {
  font-family: var(--font-mono);
  font-size: 18px;
  color: var(--accent-primary);
  word-break: break-all;
  margin-bottom: 20px;
  text-align: center;
  letter-spacing: 2px;
}

.uuid-actions {
  display: flex;
  justify-content: center;
  gap: 12px;
}

.action-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background: transparent;
  border: 1px solid var(--border-color);
  border-radius: 8px;
  color: var(--text-secondary);
  font-family: var(--font-body);
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.action-btn:hover {
  border-color: var(--accent-primary);
  color: var(--accent-primary);
}

.batch-section { border-top: 1px solid var(--border-color); padding-top: 28px; }

.batch-label {
  display: block;
  font-family: var(--font-mono);
  font-size: 11px;
  letter-spacing: 2px;
  text-transform: uppercase;
  color: var(--text-secondary);
  margin-bottom: 16px;
}

.batch-controls {
  display: flex;
  gap: 12px;
  margin-bottom: 20px;
}

.batch-input {
  width: 100px;
  padding: 12px 16px;
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  color: var(--text-primary);
  font-family: var(--font-mono);
  font-size: 14px;
  text-align: center;
}

.batch-input:focus { outline: none; border-color: var(--accent-primary); }

.batch-btn {
  padding: 12px 24px;
  background: var(--accent-primary);
  color: var(--bg-primary);
  border: none;
  border-radius: 8px;
  font-family: var(--font-body);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.batch-btn:hover { transform: translateY(-2px); box-shadow: 0 6px 16px rgba(232, 255, 0, 0.3); }

.batch-list {
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: 12px;
  padding: 16px;
  max-height: 240px;
  overflow-y: auto;
  margin-bottom: 16px;
}

.batch-item {
  font-family: var(--font-mono);
  font-size: 13px;
  color: var(--accent-success);
  padding: 8px 0;
  border-bottom: 1px solid var(--border-color);
  word-break: break-all;
}

.batch-item:last-child { border-bottom: none; }

.batch-actions {
  display: flex;
  gap: 12px;
}

.copy-all-btn, .download-btn {
  flex: 1;
  padding: 12px 20px;
  border-radius: 8px;
  font-family: var(--font-body);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
}

.copy-all-btn {
  background: transparent;
  color: var(--accent-success);
  border: 1px solid var(--accent-success);
}

.copy-all-btn:hover {
  background: var(--accent-success);
  color: var(--bg-primary);
}

.download-btn {
  background: var(--bg-secondary);
  color: var(--text-secondary);
  border: 1px solid var(--border-color);
}

.download-btn:hover {
  border-color: var(--accent-primary);
  color: var(--accent-primary);
}

@media (max-width: 640px) {
  .page-container { padding: 24px 16px 40px; }
  .page-title { font-size: 36px; }
  .uuid-card { padding: 24px; }
  .uuid-options { flex-direction: column; }
  .batch-controls { flex-direction: column; }
  .batch-input { width: 100%; }
}
</style>
